import React, {useEffect} from "react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";

import Box from "@amzn/meridian/esm/components/box";
import Column from "@amzn/meridian/esm/components/column";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Icon from "@amzn/meridian/esm/components/icon";
import Link from "@amzn/meridian/esm/components/link";
import Theme from "@amzn/meridian/esm/components/theme";
import Tile from "@amzn/meridian/esm/components/tile";

import alertFilledTokens from "@amzn/meridian-tokens/base/icon/alert-knockout";
import errorKnockout from "@amzn/meridian-tokens/base/icon/error-knockout";
import LogManager from "../../utils/logManager";
import { openUrl } from "../../utils/zoomSdkUtils";
import LearnMoreLink from "../LearnMoreLink";
import { SUMMARY_CLASSIFICATION_TYPE } from "./summary.constants";
import { THEME_COLOR } from "../../styles/colors";

const FIELD_SEPARATOR = "\u001F";

/**
 * SummaryBodyInDetailPage Component
 *
 * Renders the detailed body content of a meeting summary in the summary detail page.
 * This component encapsulates all conditional rendering logic based on `summary.summaryType`,
 * such as displaying warnings or error messages, or the full summary content including:
 * - Narrative summary
 * - Decisions (as bullet points)
 * - Next Steps (as bullet points)
 * - A footer note about AI-generated content
 *
 * Special Cases Handled:
 * - "attorney_in_attendance": show "Attorney in attendance" warning, the rest of the summary info is the same as a normal summary
 * - "server_error": Display server issue notice
 * - "short_meeting": Show warning for meetings too short to summarize
 * - "not_enough_audio": Show warning about missing conversation audio
 * - Meeting topics not approved: Link to internal documentation
 *
 * Props:
 * - `summary` (object): Required. Meeting summary metadata, narrative, decisions, and next steps.
 * - isTopicNotApproved (boolean): Indicates if the meeting topic is not approved
 */
const SummaryBodyInDetailPage = ({ summary, isTopicNotApproved }) => {

  // Message for special cases like attorney_in_attendance, not_enough_transcript etc
  const SummaryMessage = ({ icon, color, children }) => (
    <Row width="100%" widths={["16px", "fill"]} wrap="none" alignmentHorizontal="start" spacing="300" alignmentVertical="top">
      <Icon tokens={icon} color={color} />
      <Box>
        <Text type="b100">
          {children}
        </Text>
      </Box>
    </Row>
  );

  // Warning banner shown above the summary body (attorney / organizer notices), with a 24px gap below.
  const WarningBanner = ({ children }) => (
    <Column spacing="none" spacingInset="none none 450 none">
      <SummaryMessage icon={alertFilledTokens} color="warning">
        {children}
      </SummaryMessage>
    </Column>
  );

  // Section heading with a teal accent bar on the left (Meridian Tile accent,
  // with the tile's card chrome themed away so only the bar + title show).
  const SectionTitle = ({ children }) => (
    <Theme
      tokens={{
        tileBorderWidth: 0,
        tileBorderRadius: 0,
        tileBackgroundColor: "transparent",
        tileAccentWidth: 2,
        tileSpacingInset: 0,
      }}
    >
      <Tile accent={THEME_COLOR.LIGHT_TEAL} accentPosition="left">
        <Box spacingInset="none none none 200">
          <Text type="h50">{children}</Text>
        </Box>
      </Tile>
    </Theme>
  );

  // Function to render individual sections from structured summary
  const renderSection = (section, index) => {
    const { type, format, content, items, title } = section;
    
    // Log section processing for troubleshooting
    LogManager.info(`renderSection: Processing section ${index} - type="${type}", format="${format}"`);
    
    switch (format) {
      case "text":
        LogManager.info(`renderSection: Rendering text format`);
        return (
          <Box key={`section-${type}-${index}`}>
            <Text type="b100">
              {content}
            </Text>
          </Box>
        );
        
      case "markdown":
        LogManager.info(`renderSection: Rendering markdown format`);
        return (
          <Box key={`section-${type}-${index}`}>
            <Text type="b100">
              <ReactMarkdown remarkPlugins={[remarkBreaks]}>
                {content?.replace(/\n/gi, "&nbsp; \n")}
              </ReactMarkdown>
            </Text>
          </Box>
        );
        
      case "list": {
        LogManager.info(`renderSection: Rendering list format with ${items?.length || 0} items`);
        const list = (
          <Theme tokens={{ textListSpacingVertical: 0 }}>
            <Text tag="ul">
              {items?.map((item, itemIndex) => {
                const parts = item.split(/\\n|\n/);
                return (
                  <li key={itemIndex}>
                    {parts.map((part, partIndex) => (
                      <Text key={partIndex} type="b100">
                        {part}
                      </Text>
                    ))}
                  </li>
                );
              })}
            </Text>
          </Theme>
        );
        if (!title) {
          return <React.Fragment key={`section-${type}-${index}`}>{list}</React.Fragment>;
        }
        return (
          <Column key={`section-${type}-${index}`} spacing="400">
            <SectionTitle>{title}</SectionTitle>
            {list}
          </Column>
        );
      }

      case "linkList": {
        LogManager.info(`renderSection: Rendering linkList format with ${items?.length || 0} items`);
        const list = (
          <Theme tokens={{ textListSpacingVertical: 0, linkForegroundColorDefault: THEME_COLOR.TEAL_GREEN, linkForegroundColorHover: THEME_COLOR.TEAL_GREEN, linkForegroundColorPressed: THEME_COLOR.TEAL_GREEN }}>
            <Text tag="ul">
              {items?.map((item, itemIndex) => {
                const [name, url] = item.split(FIELD_SEPARATOR, 2);
                if (!url || !(url.startsWith("http://") || url.startsWith("https://"))) {
                  return null;
                }
                return (
                  <li key={itemIndex}>
                    <Text type="b100">
                      <Link
                        href={url}
                        type="primary"
                        onClick={(e) => {
                          e.preventDefault();
                          openUrl(url, "Failed to open chat link:");
                        }}
                      >
                        {name || url}
                      </Link>
                    </Text>
                  </li>
                );
              })}
            </Text>
          </Theme>
        );
        if (!title) {
          return <React.Fragment key={`section-${type}-${index}`}>{list}</React.Fragment>;
        }
        return (
          <Column key={`section-${type}-${index}`} spacing="400">
            <SectionTitle>{title}</SectionTitle>
            {list}
          </Column>
        );
      }

      default:
        LogManager.warn(`Unknown section format: ${format} for type: ${type}`);
        return null;
    }
  };

  // Function to render structured sections with proper spacing. The gap before each
  // section (24px before a list, 16px otherwise) is owned by the section's own top inset.
  const renderStructuredSections = (structuredSummary) => {
    const { sections } = structuredSummary;
    if (!sections || sections.length === 0) {
      return null;
    }

    return sections.map((section, index) => {
      const renderedSection = renderSection(section, index);
      if (!renderedSection) return null;

      const isListSection = section?.format === "list" || section?.format === "linkList";
      const spacingInset = index === 0 ? "none" : isListSection ? "450 none none none" : "400 none none none";
      return (
        <Box key={`section-wrap-${index}`} spacingInset={spacingInset}>
          {renderedSection}
        </Box>
      );
    });
  };

  const isAttorneyInAttendance = summary.summaryType === SUMMARY_CLASSIFICATION_TYPE.ATTORNEY_IN_ATTENDANCE;

  // Early return for error/warning cases (no organizer message)
  if (!summary.organizerWarningMessage && summary.iconType && !isAttorneyInAttendance) {
    return (
      <Column spacing="none">
        <SummaryMessage icon={summary.iconType === "warning" ? alertFilledTokens : errorKnockout} color={summary.iconType}>
          {summary.summaryHighlight}
        </SummaryMessage>
        {isTopicNotApproved && (
          <Box spacingInset="400 none none none">
            <LearnMoreLink />
          </Box>
        )}
      </Column>
    );
  }

  return (
    <Column spacing="none">
      {isAttorneyInAttendance && <WarningBanner>Attorney in attendance</WarningBanner>}
      {/* Organizer Warning Message - show and continue */}
      {summary.organizerWarningMessage && <WarningBanner>{summary.organizerWarningMessage}</WarningBanner>}

      {/* Render structured summary */}
      {summary.structuredSummary?.sections?.length > 0 && (
        <>
          {renderStructuredSections(summary.structuredSummary)}
        </>
      )}

      {/* AIMS Footer - only show if there's any content */}
      {summary.structuredSummary?.sections?.length > 0 && (
        <Column spacing="none" spacingInset="450 none none none">
          <Theme tokens={{ textColorPrimary: THEME_COLOR.MID_GRAY }}>
            <Text type="b100" color="primary">
              <b>Amazon Meetings Summary</b> is powered by Amazon AI. Always check for accuracy.
            </Text>
          </Theme>
        </Column>
      )}
    </Column>
  );
};

export default SummaryBodyInDetailPage;
